import { useQuery } from "convex/react";
import { api } from "../../convex/_generated/api";
import type { Doc, Id } from "../../convex/_generated/dataModel";
import PatrolReplayPlayer from "./PatrolReplayPlayer";
import { useMemo } from "react";

type DispatchDoc = Doc<"dispatches">;

export default function DispatchReplayPlayer(props: {
  dispatchId: Id<"dispatches">;
  onClose: () => void;
  assetUrls?: {
    Vehicle_IsMoving: string | null;
    Vehicle_NotMoving: string | null;
  };
  layerThumbnails?: {
    dawn?: string | null;
    day?: string | null;
    dusk?: string | null;
    night?: string | null;
    satellite?: string | null;
  };
}) {
  const dispatch = useQuery(api.dispatches.getById, { dispatchId: props.dispatchId }) as DispatchDoc | null | undefined;
  const points = useQuery(api.dispatches.getPoints, { dispatchId: props.dispatchId, limit: 5000 }) as
    | Array<{ latitude: number; longitude: number; timestamp: number }>
    | undefined;

  // Hooks must be called unconditionally. Compute memoized route/shape with safe defaults.
  const pointsKey =
    points && points.length > 0
      ? `${points.length}:${points[0].timestamp}:${points[points.length - 1].timestamp}`
      : "0";

  const route = useMemo(
    () =>
      (points || []).map((p: any) => ({
        latitude: p.latitude,
        longitude: p.longitude,
        timestamp: p.timestamp,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pointsKey]
  );

  const patrolLike = useMemo(
    () => {
      if (!dispatch) return null;
      return {
        _id: String(dispatch._id),
        vehicleName: (dispatch.vehicleName || "").trim() || "Vehicle",
        guardName: (dispatch.driverName || "").trim() || undefined,
        siteName: dispatch.siteName,
        entryTime: dispatch.startedAt,
        exitTime: dispatch.completedAt ?? dispatch.cancelledAt ?? undefined,
        duration:
          typeof dispatch.completedAt === "number"
            ? Math.floor((dispatch.completedAt - dispatch.startedAt) / 1000)
            : undefined,
        route,
      };
    },
    [dispatch, route]
  );

  if (dispatch === undefined) return null; // loading
  if (!dispatch) {
    return (
      <div className="modal-overlay" onClick={props.onClose}>
        <div className="patrol-replay-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <div className="replay-header-info">
              <h3 className="modal-title">Dispatch Replay</h3>
              <div className="replay-subtitle">Dispatch not found</div>
            </div>
            <button className="modal-close" onClick={props.onClose}>
              ×
            </button>
          </div>
        </div>
      </div>
    );
  }

  // IMPORTANT: don't coerce points to [] while loading. PatrolReplayPlayer will
  // set a sticky "no route" error on first render and won't recover when points arrive.
  if (points === undefined) return null; // loading

  if (points.length === 0) {
    return (
      <div className="modal-overlay" onClick={props.onClose}>
        <div className="patrol-replay-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <div className="replay-header-info">
              <h3 className="modal-title">Dispatch Replay</h3>
              <div className="replay-subtitle">No route points recorded for this dispatch.</div>
            </div>
            <button className="modal-close" onClick={props.onClose}>
              ×
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <PatrolReplayPlayer
      patrolVisit={patrolLike as any}
      onClose={props.onClose}
      assetUrls={props.assetUrls}
      layerThumbnails={props.layerThumbnails}
    />
  );
}
